import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, DEFAULT_LANGUAGE } from '../i18n/config';

const LanguageContext = createContext({
  currentLanguage: DEFAULT_LANGUAGE,
  changeLanguage: () => {},
  availableLanguages: LANGUAGES
});

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Initialize language from URL or localStorage
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const pathLang = pathParts[1];
    if (pathLang && LANGUAGES[pathLang]) {
      if (currentLanguage !== pathLang) {
        setCurrentLanguage(pathLang);
      }
      if (i18n.language !== pathLang) {
        i18n.changeLanguage(pathLang);
      }
    } else if (location.pathname === '/') {
      // Root path - use saved language or default
      try {
        const savedLang = localStorage.getItem('language') || DEFAULT_LANGUAGE;
        setCurrentLanguage(savedLang);
        i18n.changeLanguage(savedLang);
      } catch (error) {
        setCurrentLanguage(DEFAULT_LANGUAGE);
        i18n.changeLanguage(DEFAULT_LANGUAGE);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Change language function
  const changeLanguage = useCallback((lang) => {
    if (!LANGUAGES[lang]) return;
    
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
    
    try {
      localStorage.setItem('language', lang);
    } catch (error) {
      console.warn('localStorage not available, language preference will not persist');
    }
    
    // Update URL with new language
    let pathWithoutLang = location.pathname.replace(/^\/(en|vi|pl)/, '');
    
    // Handle locale suffix in slugs for article/activity detail pages
    // Pattern: /news/slug-vi or /activities/slug-pl
    if (/^\/(news|activities|conferences)\//.test(pathWithoutLang)) {
      // Remove existing locale suffix from slug
      pathWithoutLang = pathWithoutLang.replace(/-(en|vi|pl)$/, '');
      // Add new locale suffix (English has no suffix)
      if (lang !== 'en') {
        pathWithoutLang = `${pathWithoutLang}-${lang}`;
      }
    }
    
    navigate(`/${lang}${pathWithoutLang}${location.search}`);
  }, [i18n, navigate, location]);

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      changeLanguage,
      availableLanguages: LANGUAGES
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
